<template>
  <div class="msu-authority table-def-pg">
    <Search />
    <div class="breadcrumb-wrap">
      <div class="container"><v-breadcrumbs :items="breadcrumbs" /></div>
    </div>
    <div class="container">
      <div class="position">
        <div class="bg-white p-20">
          <template v-if="authority">
            <h1>{{ authority.name }}</h1>
            <div class="authority-meta">
              <span><strong>{{ $t('Код СОАТЕ') }}:</strong> {{ authority.soateCode }}</span>
              <span><strong>{{ $t('Область') }}:</strong> {{ authority.regionName }}</span>
              <span v-if="authority.districtName"><strong>{{ $t('Район') }}:</strong> {{ authority.districtName }}</span>
              <span><strong>{{ $t('Территория') }}:</strong> {{ authority.territoryName }}</span>
              <span v-if="authority.administrativeCenter"><strong>{{ $t('Административный центр') }}:</strong> {{ authority.administrativeCenter }}</span>
            </div>

            <h2 class="mt-8">{{ $t('Проекты нормативных правовых актов') }}</h2>
            <v-alert v-if="!authority.developerId" type="info" outlined>
              {{ $t('Орган пока не связан с учетной записью разработчика на портале') }}
            </v-alert>

            <div v-else class="search-action project-filters">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field v-model="filters.name" :label="$t('Наименование проекта')" outlined dense clearable @keyup.enter="applyFilters" />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-select v-model="filters.view" :items="references.refNpaViewForNpaType" item-value="id" item-text="transName" :label="$t('Вид проекта')" outlined dense clearable />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-select v-model="filters.status" :items="references.refStatus" item-value="id" item-text="transName" :label="$t('Статус')" outlined dense clearable />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-text-field v-model="filters.publishDateFrom" type="date" :label="$t('Дата публикации с')" outlined dense clearable />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-text-field v-model="filters.publishDateTo" type="date" :label="$t('Дата публикации по')" outlined dense clearable />
                </v-col>
              </v-row>
              <div class="form-action">
                <button class="btn btn-default blue" @click="applyFilters">{{ $t('Поиск') }}</button>
                <button class="btn btn-default white" @click="clearFilters">{{ $t('Очистить') }}</button>
              </div>
            </div>

            <div v-if="authority.developerId" class="search-result-body mt-5">
              <div class="type-column">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th v-for="item in tableHeaders" :key="item.key || item.name">
                          <Sorting
                            :item="item"
                            :currentOrderKey="orderBy"
                            @order="sortData"
                            @cancelSorting="cancelSorting"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in projects" :key="item.id">
                        <td>{{ item.view.transName }}</td>
                        <td>
                          <router-link :to="$i18nRoute({ name: 'view-npa', params: { id: item.id } })">
                            {{ item.transName }}
                          </router-link>
                        </td>
                        <td>{{ commentCount(item.id) }} / {{ answerCount(item.id) }}</td>
                        <td>{{ formatDate(item.publish_date) }}</td>
                        <td>{{ formatDate(item.discussion_period_date_to) }}</td>
                        <td>{{ item.status.transName }}</td>
                      </tr>
                      <tr v-if="projects.length === 0">
                        <td colspan="6" class="text-center">{{ $t('По вашему запросу ничего не найдено') }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
              <div class="search-result-footer">
                <p>{{ $t('Общее количество НПА') }}: {{ paginationData.totalCount || 0 }}</p>
              </div>
              <v-pagination v-if="paginationData.pageCount > 1" v-model="page" :length="paginationData.pageCount" :total-visible="7" @input="loadProjects" />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapMutations } from 'vuex'
import Search from '@/components/Search'
import Sorting from '@/components/app/Sorting'
import CMS from '@/model/cms'
import Msu from '@/model/msu'
import SearchModel from '@/model/search'
import { Trans } from '@/plugins/Translation'

export default {
  components: { Search, Sorting },
  data: () => ({
    authority: null,
    filters: {},
    references: { refNpaViewForNpaType: [], refStatus: [] },
    projects: [],
    commentsCnt: {},
    answersCnt: {},
    paginationData: {},
    page: 1,
    orderBy: 'publishDate',
    orderDir: 'desc',
    breadcrumbs: []
  }),
  computed: {
    tableHeaders() {
      return [
        { name: this.$t('Вид проекта'), key: 'view' },
        { name: this.$t('Наименование'), key: 'name' },
        {
          name: this.$t('Комментарии'),
          keys: ['commentsCount', 'answersCount'],
          children: [
            { name: this.$t('Комментарии'), key: 'commentsCount' },
            { name: this.$t('Ответы'), key: 'answersCount' }
          ]
        },
        { name: this.$t('Дата публикации'), key: 'publishDate' },
        { name: this.$t('Дата окончания обсуждения'), key: 'discussionPeriodDateTo' },
        { name: this.$t('Статус'), key: 'status' }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    ...mapMutations(['setLoading']),
    async loadData() {
      this.setLoading(true)
      try {
        const responses = await Promise.all([
          Msu.getAuthority(this.$route.params.id),
          CMS.getNpaData(['refNpaViewForNpaType', 'refStatus'])
        ])
        this.authority = responses[0].data
        this.references = responses[1].data
        this.breadcrumbs = [
          { text: this.$i18n.t('Главная'), disabled: false, href: Trans.currentLocaleUrl + '/' },
          { text: this.$i18n.t('Проекты МСУ'), disabled: false, href: Trans.currentLocaleUrl + '/msu' },
          { text: this.authority.name, disabled: true }
        ]
        if (this.authority.developerId) await this.loadProjects(false)
      } finally {
        this.setLoading(false)
      }
    },
    async loadProjects(showLoading = true) {
      if (showLoading) this.setLoading(true)
      try {
        const response = await SearchModel.search({
          ...this.filters,
          developer: this.authority.developerId,
          page: this.page,
          limit: 20,
          orderBy: this.orderBy,
          orderDir: this.orderDir
        })
        this.projects = response.data.items
        this.commentsCnt = response.data.commentsCnt
        this.answersCnt = response.data.answersCnt
        this.paginationData = response.data.paginationData
      } finally {
        if (showLoading) this.setLoading(false)
      }
    },
    applyFilters() {
      this.page = 1
      this.loadProjects()
    },
    clearFilters() {
      this.filters = {}
      this.applyFilters()
    },
    sortData(sort) {
      this.orderBy = sort.key
      this.orderDir = sort.value
      this.page = 1
      this.loadProjects()
    },
    cancelSorting() {
      this.orderBy = 'publishDate'
      this.orderDir = 'desc'
      this.page = 1
      this.loadProjects()
    },
    commentCount(id) {
      return this.commentsCnt[id] ? this.commentsCnt[id].cntComments : 0
    },
    answerCount(id) {
      return this.answersCnt[id] ? this.answersCnt[id].cntAnswers : 0
    },
    formatDate(value) {
      return value ? moment(value).format('DD.MM.YYYY') : '—'
    }
  }
}
</script>

<style scoped>
.authority-meta { display: flex; flex-wrap: wrap; gap: 10px 28px; padding: 18px 0; border-bottom: 1px solid #e1e1e1; }
.authority-meta span { color: #49525d; }
.project-filters { margin-top: 20px; }
.project-filters .v-input { margin-bottom: -16px; }
.type-column { overflow-x: auto; }
</style>
